/**
 * Redirect-based OAuth: the state that has to survive the round trip.
 *
 * `signInWithRedirect` destroys the page. The tab navigates to Google, comes
 * back on a fresh load, and every ref, every piece of component state and every
 * callback the caller was holding is gone. Anything the return path needs must
 * therefore be on disk before the redirect starts, which is what this module
 * owns: a marker in `sessionStorage` (same tab, same origin, dies with the tab)
 * naming the attempt, plus a single memoized read of the result on return.
 *
 * `getRedirectResult` yields the credential exactly once per page load, so the
 * two consumers — AuthProvider, which needs to know this load is a completed
 * sign-in, and SignInForm, which owes its surface an `onSuccess` — share one
 * promise rather than each calling it and one of them getting null.
 *
 * The marker is claimed on the first read, not on success: a load that came
 * back empty has still consumed it, and leaving it behind would make the next
 * load believe it too had just returned from Google.
 */

import { getRedirectResult, type Auth, type User } from "firebase/auth";

const PENDING_KEY = "outclose:authRedirect";
/**
 * Sign-in that has been sitting unfinished this long is an attempt the user
 * walked away from — a marker that outlives it must not claim the next load.
 */
const PENDING_TTL_MS = 15 * 60 * 1000;

/**
 * Set when a redirect came back with nothing, which in practice means the
 * browser refused the auth handler storage it needs. Retrying would redirect
 * into the same wall, so the rest of the session falls back to the popup.
 */
const UNSUPPORTED_KEY = "outclose:authRedirectUnsupported";

/** Only Google today; Apple popups already succeed where Google's don't. */
export type RedirectAuthProvider = "google";

/**
 * Everything about the attempt that has to outlive the page.
 *
 * The dialog is why this is more than a boolean. An inline form re-mounts on
 * the far side and can run its own success handling, but a dialog opened from
 * an arbitrary page is gone — along with the `onSuccess` closure it was holding
 * and the `asSignUp` flag that decides whether a session with no profile means
 * "welcome" or "we don't know this account". Both are recorded here in a form
 * that survives serialization: a path instead of a callback, a flag instead of
 * component state.
 */
export interface AuthRedirectIntent {
  provider: RedirectAuthProvider;
  /**
   * Which kind of surface started it. Only `"dialog"` may drive the dialog's
   * post-sign-in steering on return — an inline form that ran the dialog's
   * completion would fire `onSuccess` from whichever caller opened the dialog
   * last and throw the user out of the flow they were in.
   */
  surface: "dialog" | "inline";
  /**
   * Where to land once signed in, mirroring what the surface's `onSuccess`
   * would have done. Omitted when the right answer is "stay where you are",
   * which is already what the redirect does on its own.
   */
  returnTo?: string;
  /** Whether the surface was sign-up flavored. Drives the same branch as the
   *  dialog's own `asSignUp` option, which is not around to be read. */
  asSignUp?: boolean;
}

interface PendingAuthRedirect extends AuthRedirectIntent {
  /** Epoch ms, for the staleness check above. */
  at: number;
}

export interface RedirectSignInOutcome {
  provider: RedirectAuthProvider;
  /** The signed-in user, or null when the round trip produced nothing. */
  user: User | null;
  /** Firebase error code, `"no-user"` for an empty return, else null. */
  errorCode: string | null;
}

/**
 * Records that a redirect is about to start. Called immediately before
 * `signInWithRedirect`, since after it the page is no longer ours.
 */
export function markAuthRedirectPending(intent: AuthRedirectIntent): void {
  try {
    sessionStorage.setItem(
      PENDING_KEY,
      JSON.stringify({ ...intent, at: Date.now() } satisfies PendingAuthRedirect),
    );
  } catch {
    // Private mode. The sign-in still completes; only the surface's own
    // success handling on return is lost, and the flow recovers from the
    // session appearing.
  }
}

function readPending(): PendingAuthRedirect | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingAuthRedirect;
    if (!parsed?.provider) return null;
    if (Date.now() - parsed.at > PENDING_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearPending(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

/** `undefined` = not yet read this load; `null` = read, and there was none. */
let latchedIntent: AuthRedirectIntent | null | undefined = undefined;

/**
 * The attempt this page load is the far side of, or null for an ordinary load.
 *
 * Synchronous, and latched on the first call rather than re-read: consuming the
 * result claims the marker, and callers that mount later — a lazily-loaded quiz
 * route restoring the step the user left from, say — would otherwise find it
 * gone and conclude this was an ordinary load. The first caller runs during
 * AuthProvider's render, before any effect can clear anything.
 */
export function getAuthRedirectIntent(): AuthRedirectIntent | null {
  if (latchedIntent === undefined) {
    const pending = readPending();
    latchedIntent = pending
      ? {
          provider: pending.provider,
          surface: pending.surface,
          returnTo: pending.returnTo,
          asSignUp: pending.asSignUp,
        }
      : null;
  }
  return latchedIntent;
}

/** Whether this page load is the far side of a redirect sign-in. */
export function hasPendingAuthRedirect(): boolean {
  return getAuthRedirectIntent() !== null;
}

export function markRedirectUnsupported(): void {
  try {
    sessionStorage.setItem(UNSUPPORTED_KEY, "1");
  } catch {
    // ignore
  }
}

/** A redirect already came back empty in this tab — use the popup instead. */
export function isRedirectUnsupported(): boolean {
  try {
    return sessionStorage.getItem(UNSUPPORTED_KEY) === "1";
  } catch {
    return false;
  }
}

let outcomePromise: Promise<RedirectSignInOutcome | null> | null = null;

/**
 * The result of the redirect this page load came back from, or null if it
 * didn't. Memoized: every caller gets the same answer, and `getRedirectResult`
 * is only ever asked once.
 */
export function consumeRedirectSignIn(
  auth: Auth,
): Promise<RedirectSignInOutcome | null> {
  if (outcomePromise) return outcomePromise;

  // Latch the answer before the marker goes, for everything that asks later.
  hasPendingAuthRedirect();
  const pending = readPending();
  clearPending();
  if (!pending) {
    outcomePromise = Promise.resolve(null);
    return outcomePromise;
  }

  const { provider } = pending;
  outcomePromise = getRedirectResult(auth)
    .then((credential): RedirectSignInOutcome => {
      if (credential?.user) {
        return { provider, user: credential.user, errorCode: null };
      }
      // Firebase resolved with nothing: the credential never made it back.
      // Almost always third-party storage being blocked for an auth handler
      // that isn't same-origin with the app — see the `/__/auth/*` proxy in
      // client/functions. Mark it so the session stops trying.
      markRedirectUnsupported();
      return { provider, user: null, errorCode: "no-user" };
    })
    .catch((e: unknown): RedirectSignInOutcome => {
      const code = (e as { code?: string })?.code ?? "unknown";
      return { provider, user: null, errorCode: code };
    });

  return outcomePromise;
}

/** Test seam — drops the memoized state so each test starts clean. */
export function resetRedirectSignInForTests(): void {
  outcomePromise = null;
  latchedIntent = undefined;
}
